import React from 'react'
import { Link } from 'react-router-dom'
import { Dropdown } from '@components/Autocomplete'
import map from 'lodash/map'
import moment from 'moment'
import find from 'lodash/find'
import { observer, inject } from 'mobx-react'
import CommentIcon from '@components/Icon/Comment'
import EmployeeInitials from '@components/EmployeeInitials'
import sumBy from 'lodash/sumBy'
import ChevronIcon from '@components/icons/ChevronIcon'
import EditIcon from '@components/Icon/Edit'
import Button from '@components/Button'
import RichTextEditor from '@components/RichTextEditor'
const COMMENTS_WIDTH = 200

const Sort = observer(({ store, by }) => {
  const style = { float: 'right', cursor: 'pointer', marginRight: -10 }
  if (store.query.sortBy === by) {
    if (store.query.sortOrder === 'asc') {
      return (
        <div
          style={style}
          onClick={() => store.search({ sortBy: by, sortOrder: 'desc' })}
          title='Click to reverse sorting order'
        >
          <ChevronIcon down />
        </div>
      )
    } else {
      return (
        <div
          style={style}
          title='Click to stop sorting by this column'
          onClick={() =>
            store.search({ sortBy: 'created_at', sortOrder: 'desc' })
          }
        >
          <ChevronIcon up />
        </div>
      )
    }
  } else {
    return (
      <div
        style={style}
        className='hover-item'
        title='Click to sort by this column'
        onClick={() => store.search({ sortBy: by, sortOrder: 'asc' })}
      >
        <ChevronIcon down />
      </div>
    )
  }
})

const Table = observer(
  ({
    projects,
    actions,
    priorities,
    updateAction,
    store,
    updatePriority,
    now,
    openComments,
    commentsVisible,
    expandProject,
    userStore
  }) => {
    const userId = userStore.user.id
    return (
      <table className='table-wrapper'>
        <thead>
          <tr>
            <th
              className={`hover-container${
                store.query.sortBy === 'projectName' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'projectName'} /> Project Name
            </th>
            <th
              className={`hover-container${
                store.query.sortBy === 'priority' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'priority'} />
              Priority
            </th>
            <th>Handlers</th>
            <th>Recruiters</th>
            <th>Req</th>
            <th>Dv</th>
            <th>Sch</th>
            <th>Iv</th>
            <th
              className={`hover-container${
                store.query.sortBy === 'action' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'action'} />
              Action
            </th>
            <th>Client</th>
            <th
              className={`hover-container${
                store.query.sortBy === 'office' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'office'} />
              Office
            </th>
            <th
              className={`hover-container${
                store.query.sortBy === 'extra1' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'extra1'} />
              Extra 1
            </th>
            <th
              className={`hover-container${
                store.query.sortBy === 'extra2' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'extra2'} />
              Extra 2
            </th>
            <th
              className={`hover-container${
                store.query.sortBy === 'extra3' ? ' sorted' : ''
              }`}
            >
              <Sort store={store} by={'extra3'} />
              Extra 3
            </th>
            <th colSpan={commentsVisible ? 1 : 2}>
              {!commentsVisible && 'Comments'}
            </th>
          </tr>
        </thead>
        <tbody className='project-management-table-body'>
          {!projects.length && <td colSpan={13}>No projects found</td>}
          {Boolean(projects.length) && (
            <tr>
              <td colSpan={4}>
                <strong>Totals</strong>
              </td>
              <td>
                <strong>{sumBy(projects, 'req')}</strong>
              </td>
              <td>
                <strong>{sumBy(projects, 'delivered')}</strong>
              </td>
              <td>
                <strong>{sumBy(projects, 'scheduled')}</strong>
              </td>
              <td>
                <strong>{sumBy(projects, 'interviewed')}</strong>
              </td>
              <td colSpan={8} />
            </tr>
          )}
          {Boolean(projects.length) &&
            projects.map(project => (
              <>
                <tr key={project.id}>
                  <td className='project-management-table-name'>
                    <div
                      onClick={() => expandProject(project)}
                      className='project-management-expand'
                    >
                      <ChevronIcon
                        right={!project.segments}
                        down={project.segments}
                      />
                    </div>
                    <Link to={`/project/${project.id}`}>{project.name}</Link>
                  </td>
                  <td style={{ padding: '0px 15px 0px 0px' }}>
                    <Dropdown
                      disabled={!project.userIsHandler}
                      title={
                        !project.userIsHandler
                          ? 'You need to be handler for this project to change priority'
                          : 'Change priority'
                      }
                      valueIsId
                      fullRow
                      color={
                        find(priorities, a => a.id === project.priorityId).color
                      }
                      selectedItem={project.priorityId}
                      options={priorities}
                      onChange={val => updatePriority(project, val.id)}
                      itemToString={i => (
                        <div
                          style={{
                            background: i.color,
                            marginTop: -8,
                            marginBottom: -8,
                            marginRight: -8,
                            marginLeft: -8,
                            padding: 10,
                            fontSize: 12
                          }}
                        >
                          {i.name}
                        </div>
                      )}
                    />
                  </td>
                  <td style={{ paddingRight: '5px' }}>
                    {renderEmployees(project.handlers)}
                  </td>
                  <td style={{ paddingRight: '5px', width: 140 }}>
                    {renderRecruiters(project.recruiters, project.id, store)}
                  </td>
                  <td>
                    <span title='Required'>
                      {project.req ? project.req : 0}
                    </span>
                  </td>
                  <td>
                    <span title='Delivered'>
                      {project.delivered ? project.delivered : 0}
                    </span>
                  </td>
                  <td>
                    <span title='Scheduled'>
                      {project.scheduled ? project.scheduled : 0}
                    </span>
                  </td>
                  <td>
                    <span title='Interviewed'>
                      {project.interviewed ? project.interviewed : 0}
                    </span>
                  </td>
                  <td style={{ padding: '0px 25px 0px 0px' }}>
                    <Dropdown
                      disabled={!project.userIsHandler}
                      valueIsId
                      title={
                        !project.userIsHandler
                          ? 'You need to be handler for this project to change action'
                          : 'Change action'
                      }
                      fullRow
                      color={
                        find(actions, a => a.id === project.actionId).color
                      }
                      selectedItem={project.actionId}
                      options={actions}
                      onChange={val => updateAction(project, val.id)}
                      itemToString={i => (
                        <div
                          style={{
                            background: i.color,
                            marginTop: -8,
                            marginBottom: -8,
                            marginRight: -8,
                            marginLeft: -8,
                            padding: 10,
                            fontSize: 12
                          }}
                        >
                          {i.name}
                        </div>
                      )}
                    />
                  </td>
                  <td>
                    {map(project.clients.slice(0, 2), (c, i) => (
                      <div key={c.id}>
                        {c.firstName} {c.lastName}
                      </div>
                    ))}
                  </td>
                  <td title='Office Name'>
                    <div
                      style={{
                        maxWidth: 200,
                        width: 200,
                        textOverflow: 'ellipsis'
                      }}
                    >
                      <Link to={`/office/${project.officeId}`}>
                        {project.office}
                      </Link>
                    </div>
                    <div
                      title={
                        moment(now)
                          .tz(project.time)
                          .format(`UTC Z `) + project.time
                      }
                    >
                      {project.country} -{' '}
                      <strong>
                        {moment(now)
                          .tz(project.time)
                          .format('h:mm A (z)')}
                      </strong>
                    </div>
                  </td>
                  {renderExtraColumns(project, store)}
                  {!commentsVisible && (
                    <td style={{ paddingRight: 0 }}>
                      <div
                        style={{
                          width: COMMENTS_WIDTH,
                          maxHeight: 34,
                          overflow: 'hidden'
                        }}
                      >
                        {project.comment}
                      </div>
                    </td>
                  )}
                  <td>
                    <div onClick={() => openComments(project)}>
                      <CommentIcon />
                    </div>
                  </td>
                </tr>
                {project.segments &&
                  project.segments.map(s => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td />
                      <td />
                      <td>{renderEmployees(s.recruiters)}</td>
                      <td>{s.required || 0}</td>
                      <td>{s.delivered || 0}</td>
                      <td>{s.scheduled || 0}</td>
                      <td>{s.interviewed || 0}</td>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                    </tr>
                  ))}
              </>
            ))}
        </tbody>
      </table>
    )
  }
)

function renderExtraColumns (project, store) {
  return [...Array(3).keys()].map(i => (
    <td className='project-management-extra-column'>
      {store.editExtraField === `${project.id}_extra${i + 1}` ? (
        renderExtraEditMode(project, i, store)
      ) : (
        <>
          <div
            onClick={() => {
              store.extra = [...Array(3).keys()].map(
                i => project[`extra${i + 1}`]
              )
              store.editExtraField = `${project.id}_extra${i + 1}`
            }}
            className='project-management-extra-expand'
          >
            <EditIcon />
          </div>
          <div
            onClick={() => {
              store.extra = [...Array(3).keys()].map(
                i => project[`extra${i + 1}`]
              )
              store.editExtraField = `${project.id}_extra${i + 1}`
            }}
            className='project-management-extra-content'
            dangerouslySetInnerHTML={{ __html: project[`extra${i + 1}`] }}
          />
        </>
      )}
    </td>
  ))
}

function renderExtraEditMode (project, i, store) {
  return (
    <div className='project-management-overlay'>
      <div className='project-management-overlay-container'>
        <h3>
          {project.name} - Extra {i + 1}
        </h3>
        <div
          className='project-management-overlay-content'
          style={{ marginTop: 50, marginBottom: 10 }}
        >
          <RichTextEditor
            mentions
            value={store.extra[i]}
            onChange={val => (store.extra[i] = val)}
            config={{
              charCounterCount: true,
              charCounterMax: 200,
              toolbarButtons: ['insertLink'],
              htmlAllowedStyleProps: ['text-align', 'vertical-align']
            }}
          />
        </div>
        <div className='project-management-overlay-actions'>
          <Button
            onClick={async () => {
              await store.updateAction(
                project,
                project.actionId,
                store.extra[0],
                store.extra[1],
                store.extra[2]
              )
              store.editExtraField = null
              store.getAll()
            }}
            block
            primary
          >
            Save
          </Button>
          <Button onClick={() => (store.editExtraField = null)} block secondary>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

function renderRecruiters (emp, id, store) {
  let empList = emp.slice(0, 5)

  if (id === store.expandedRecruiters) {
    empList = emp
  }

  return (
    <div style={{ maxWidth: 110 }}>
      {empList.map(e => (
        <EmployeeInitials key={e.id} employee={e} />
      ))}
      {id !== store.expandedRecruiters && emp.length > 5 ? (
        <div
          className='recruiters-expand'
          onClick={() => {
            store.expandedRecruiters = id
          }}
        >
          More
        </div>
      ) : (
        emp.length > 5 && (
          <div
            className='recruiters-expand'
            onClick={() => {
              store.expandedRecruiters = null
            }}
          >
            Less
          </div>
        )
      )}
    </div>
  )
}

function renderEmployees (emp) {
  return (
    <div style={{ maxWidth: 110 }}>
      {emp.slice(0, 3).map(e => (
        <EmployeeInitials key={e.id} employee={e} />
      ))}
    </div>
  )
}

export default inject('userStore')(Table)
